import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    UserGroupIcon,
    MapIcon,
    CubeIcon,
    ClockIcon,
    ArrowPathIcon,
    ChartBarIcon,
} from '@heroicons/react/24/outline';

import KPICard from '../components/KPICard';
import MiniMap from '../components/dashboard/MiniMap';
import { useAuth } from '../contexts/AuthContext';
import { CardSkeleton } from '../components/skeletons';
import { apiGet, subscribeToDataInvalidation } from '../utils/api';
import { API_URL } from '../api/config';

interface AgronomistKPIData {
    farmers_managed: number;
    active_compost_batches: number;
    upcoming_training_sessions: number;
    field_visits_today: number;
}

interface FarmerPlot {
    id: number;
    full_name: string;
    household_type: string;
    plot_boundary: any[];
    location_coordinates: any;
    plot_size_hectares: number;
}

interface CompostBatch {
    id: number;
    batch_name: string;
    source_name?: string;
    status: string;
    start_date: string;
    target_amount_kg: number;
    actual_amount_kg: number;
    workday_count: number;
    avg_quality: number;
}

const AgronomistDashboard = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [kpiData, setKpiData] = useState<AgronomistKPIData | null>(null);
    const [farmers, setFarmers] = useState<FarmerPlot[]>([]);
    const [compostBatches, setCompostBatches] = useState<CompostBatch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

    const refreshData = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchKPIs(),
                fetchFarmers(),
                fetchCompostBatches(),
            ]);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Refresh failed", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshData();
        const intervalId = setInterval(refreshData, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [refreshData]);

    useEffect(() => {
        return subscribeToDataInvalidation(() => {
            refreshData();
        });
    }, [refreshData]);

    const fetchKPIs = async () => {
        try {
            const data = await apiGet<AgronomistKPIData>('/api/agronomist/dashboard/kpis');
            setKpiData(data);
        } catch (err) {
            console.error('Failed to fetch KPIs:', err);
            setError('Failed to load dashboard data');
        }
    };

    const fetchFarmers = async () => {
        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/agronomist/farmers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFarmers(data.map((f: any) => ({
                    ...f,
                    plot_boundary: (() => {
                        let coords = f.plot_boundary;
                        if (typeof coords === 'string') {
                            try {
                                coords = JSON.parse(coords);
                            } catch (e) {
                                return [];
                            }
                        }
                        return Array.isArray(coords) ? coords.map((c: any) => {
                            if (Array.isArray(c)) {
                                return { lat: Number(c[0]), lng: Number(c[1]) };
                            }
                            return {
                                lat: Number(c.lat || c.latitude),
                                lng: Number(c.lng || c.longitude)
                            };
                        }) : [];
                    })(),
                    location_coordinates: (() => {
                        let coords = f.location_coordinates;
                        if (typeof coords === 'string') {
                            try {
                                coords = JSON.parse(coords);
                            } catch (e) {
                                return null;
                            }
                        }
                        if (coords && !isNaN(Number(coords.lat)) && !isNaN(Number(coords.lng))) {
                            return {
                                lat: Number(coords.lat),
                                lng: Number(coords.lng)
                            };
                        }
                        return null;
                    })()
                })));
            }
        } catch (err) {
            console.error('Failed to fetch farmers:', err);
        }
    };

    const fetchCompostBatches = async () => {
        try {
            const data = await apiGet<CompostBatch[]>('/api/agronomist/compost/batches');
            setCompostBatches(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch compost batches:', err);
        }
    };

    if (loading && !kpiData) {
        return (
            <div className="space-y-6">
                <div className="bg-gray-100 rounded-2xl p-8 animate-pulse">
                    <div className="h-8 w-64 bg-gray-200 rounded mb-3" />
                    <div className="h-4 w-96 bg-gray-200 rounded" />
                </div>
                <CardSkeleton count={5} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-800">{error}</p>
                <button
                    onClick={refreshData}
                    className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    const totalLandArea = farmers.reduce((sum, f) => sum + (Number(f.plot_size_hectares) || 0), 0);
    const mappedFields = farmers.filter(f => Array.isArray(f.plot_boundary) && f.plot_boundary.length >= 3).length;
    const activeBatches = compostBatches.filter(b => b.status === 'in_progress' || b.status === 'curing');

    return (
        <div className="space-y-8 pb-12">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
                        {t('dashboard.agronomist_overview', 'Agronomist Dashboard')}
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Welcome back, {user?.full_name} • {t('dashboard.last_updated')}: {lastUpdated.toLocaleTimeString()}
                    </p>
                </div>

                <button
                    onClick={refreshData}
                    className="bg-white p-2.5 rounded-lg border border-slate-200 text-slate-500 hover:text-brand-blue-600 hover:border-brand-blue-200 transition-all shadow-sm"
                    title="Refresh Data"
                >
                    <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* KPI Summary Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <KPICard
                    title="Assigned Farmers"
                    value={kpiData?.farmers_managed.toString() || '0'}
                    icon={<UserGroupIcon />}
                    color="blue"
                />
                <KPICard
                    title="Mapped Fields"
                    value={String(mappedFields)}
                    icon={<MapIcon />}
                    color="purple"
                />
                <KPICard
                    title="Active Compost"
                    value={kpiData?.active_compost_batches.toString() || '0'}
                    subtitle="Batches"
                    icon={<CubeIcon />}
                    color="green"
                />
                <KPICard
                    title="Total Land Area"
                    value={`${totalLandArea.toFixed(1)} ha`}
                    icon={<ChartBarIcon />}
                    color="amber"
                />
                <KPICard
                    title="Field Visits Today"
                    value={kpiData?.field_visits_today.toString() || '0'}
                    icon={<ClockIcon />}
                    color="indigo"
                />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Field Map & Farmers */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Field Map Preview */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <MapIcon className="w-5 h-5 text-brand-blue-600" />
                                Field Overview
                            </h3>
                            <a
                                href="/dashboard/maps"
                                className="text-sm text-brand-blue-600 hover:text-brand-blue-700 font-medium"
                            >
                                View Full Map →
                            </a>
                        </div>
                        <div className="h-96 rounded-lg overflow-hidden">
                            <MiniMap cohorts={[]} warehouses={[]} farmers={farmers} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                            <div className="bg-slate-50 rounded-lg p-2">
                                <p className="text-slate-500">Total Farmers</p>
                                <p className="font-bold text-slate-800">{farmers.length}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-2">
                                <p className="text-slate-500">Total Land</p>
                                <p className="font-bold text-slate-800">{totalLandArea.toFixed(1)} ha</p>
                            </div>
                        </div>
                    </div>

                    {/* Recent Farmers */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <UserGroupIcon className="w-5 h-5 text-brand-blue-600" />
                                Assigned Farmers
                            </h3>
                            <a
                                href="/dashboard/farmers"
                                className="text-sm text-brand-blue-600 hover:text-brand-blue-700 font-medium"
                            >
                                View All →
                            </a>
                        </div>
                        {farmers.length > 0 ? (
                            <div className="space-y-2">
                                {farmers.slice(0, 5).map((farmer) => (
                                    <div key={farmer.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition">
                                        <div>
                                            <p className="text-sm font-medium text-slate-800">{farmer.full_name}</p>
                                            <p className="text-xs text-slate-500">{Number(farmer.plot_size_hectares || 0).toFixed(2)} ha mapped</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-semibold text-slate-800">
                                                {Number(farmer.plot_size_hectares || 0).toFixed(2)} ha
                                            </p>
                                            <p className="text-xs text-slate-500 capitalize">{farmer.household_type}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center">
                                <p className="text-sm font-bold text-slate-600">No farmers assigned yet</p>
                                <p className="mt-1 text-xs text-slate-500">Ask your Project Manager to assign farms or farmers to you.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Field Coverage & Compost */}
                <div className="space-y-8">
                    {/* Field Coverage */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800">Field Coverage</h3>
                            <a
                                href="/dashboard/farmers"
                                className="text-sm text-brand-blue-600 hover:text-brand-blue-700 font-medium"
                            >
                                Manage →
                            </a>
                        </div>
                        {farmers.length > 0 ? (
                            <div className="space-y-3">
                                {farmers.slice(0, 5).map((farmer) => (
                                    <div key={farmer.id} className="p-3 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg">
                                        <p className="text-sm font-semibold text-slate-800">{farmer.full_name}</p>
                                        <p className="text-xs text-slate-600 mt-1">{farmer.household_type?.replace('_', ' ') || 'Farmer'}</p>
                                        <div className="flex justify-between text-xs text-slate-500 mt-2">
                                            <span>{Array.isArray(farmer.plot_boundary) && farmer.plot_boundary.length >= 3 ? 'Boundary mapped' : 'Needs mapping'}</span>
                                            <span>{Number(farmer.plot_size_hectares || 0).toFixed(1)} ha</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 text-center py-4">No assigned farmers</p>
                        )}
                    </div>

                    {/* Active Compost Batches */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <CubeIcon className="w-5 h-5 text-green-600" />
                                Compost Progress
                            </h3>
                            <a
                                href="/dashboard/compost"
                                className="text-sm text-brand-blue-600 hover:text-brand-blue-700 font-medium"
                            >
                                View All →
                            </a>
                        </div>
                        {activeBatches.length > 0 ? (
                            <div className="space-y-3">
                                {activeBatches.slice(0, 4).map((batch) => {
                                    const progress = batch.target_amount_kg > 0
                                        ? Math.min((batch.actual_amount_kg / batch.target_amount_kg) * 100, 100)
                                        : 0;
                                    return (
                                        <div key={batch.id} className="p-3 bg-slate-50 rounded-lg">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <p className="text-sm font-medium text-slate-800">{batch.batch_name}</p>
                                                    <p className="text-xs text-slate-500">{batch.source_name || 'Field compost batch'}</p>
                                                </div>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${batch.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                                                    }`}>
                                                    {batch.status.replace('_', ' ').toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="w-full bg-slate-200 rounded-full h-2 mb-1">
                                                <div
                                                    className="h-2 rounded-full bg-green-600"
                                                    style={{ width: `${progress}%` }}
                                                ></div>
                                            </div>
                                            <div className="flex justify-between text-xs text-slate-600">
                                                <span>{batch.actual_amount_kg} / {batch.target_amount_kg} kg</span>
                                                <span>{progress.toFixed(0)}%</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 text-center py-4">No active batches</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgronomistDashboard;
